/**
 * CNV-2913 — PDP reviews sections powered by the Okendo storefront API.
 *
 * Shared data layer + two web components:
 * - <claims-reviews>   (sections/s-claims-reviews.liquid): appends written
 *   Okendo reviews as cards into each claim row (Breathing/Performance/…).
 * - <product-reviews>  (sections/product-reviews.liquid): media strip,
 *   category chips, review list with load more, media lightbox.
 *
 * Reviews are third-party UGC — rendered with textContent only, never innerHTML.
 * Both sections load this same file; the guard below keeps it single-run.
 */
(() => {
	if (window.__pdpReviewsInit) return;
	window.__pdpReviewsInit = true;

	const API_BASE = "https://api.okendo.io/v1";
	const CACHE_TTL = 30 * 60 * 1000; // 30 min
	const MAX_PAGES = 8; // hard cap: 8 * 25 = 200 reviews per load

	// ---------------------------------------------------------------- data —

	const store = (window.__okendoReviewsStore = window.__okendoReviewsStore || {
		promises: {},
	});

	const cacheGet = (key) => {
		try {
			const raw = sessionStorage.getItem(key);
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			if (!parsed.ts || Date.now() - parsed.ts > CACHE_TTL) return null;
			return parsed.data;
		} catch (e) {
			return null;
		}
	};

	const cacheSet = (key, data) => {
		try {
			sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
		} catch (e) {
			/* storage full / private mode — ignore */
		}
	};

	const fetchJson = async (url) => {
		const res = await fetch(url);
		if (!res.ok) throw new Error(`Okendo API ${res.status} for ${url}`);
		return res.json();
	};

	const fetchAllReviews = async (base) => {
		let url = `${base}/reviews?limit=25&orderBy=${encodeURIComponent(
			"date desc"
		)}`;
		const all = [];
		for (let page = 0; page < MAX_PAGES && url; page++) {
			const json = await fetchJson(url);
			all.push(...(json.reviews || []));
			url = json.nextUrl ? `${API_BASE}${json.nextUrl}` : null;
		}
		return all;
	};

	const fetchMedia = async (base) => {
		try {
			const json = await fetchJson(`${base}/review_media?limit=25`);
			return json.media || [];
		} catch (e) {
			return []; // media strip is optional — don't fail the whole section
		}
	};

	const loadOkendoData = (subscriberId, productId) => {
		const key = `${subscriberId}:${productId}`;
		if (!store.promises[key]) {
			store.promises[key] = (async () => {
				const cacheKey = `okReviews:${productId}`;
				const cached = cacheGet(cacheKey);
				if (cached) return cached;

				const base = `${API_BASE}/stores/${subscriberId}/products/shopify-${productId}`;
				const [reviews, media] = await Promise.all([
					fetchAllReviews(base),
					fetchMedia(base),
				]);
				const data = { reviews, media };
				cacheSet(cacheKey, data);
				return data;
			})();
			// allow a retry on failure instead of caching the rejection
			store.promises[key].catch(() => {
				delete store.promises[key];
			});
		}
		return store.promises[key];
	};

	// ------------------------------------------------------------ classify —

	const parseListField = (value) =>
		String(value || "")
			.split(/[\n,]+/)
			.map((item) => item.trim().toLowerCase())
			.filter(Boolean);

	const normalizeCategories = (categories) =>
		(categories || []).map((cat) => ({
			name: cat.name,
			slug: cat.slug,
			keywords: parseListField(cat.keywords),
			includeIds: parseListField(cat.includeIds),
			excludeIds: parseListField(cat.excludeIds),
		}));

	// Returns a Set of category slugs the review belongs to.
	// Exclusive buckets: a review lands in exactly ONE category — its best
	// keyword match (occurrence-weighted; ties go to the earlier category).
	// Admin include-IDs / Okendo tags are explicit overrides and win outright
	// (and may place a review in several categories on purpose).
	const classifyReview = (review, categories) => {
		const slugs = new Set();
		const id = String(review.reviewId || "").toLowerCase();
		const haystack = `${review.title || ""} ${review.body || ""}`.toLowerCase();
		const tags = Array.isArray(review.tags)
			? review.tags.map((t) => String(t).toLowerCase())
			: [];

		categories.forEach((cat) => {
			if (cat.excludeIds.includes(id)) return;
			if (cat.includeIds.includes(id) || tags.includes(cat.name.toLowerCase())) {
				slugs.add(cat.slug);
			}
		});
		if (slugs.size) return slugs;

		let best = null;
		let bestScore = 0;
		categories.forEach((cat) => {
			if (cat.excludeIds.includes(id)) return;
			const score = cat.keywords.reduce(
				(n, kw) => n + (haystack.split(kw).length - 1),
				0
			);
			if (score > bestScore) {
				bestScore = score;
				best = cat.slug;
			}
		});
		if (best) slugs.add(best);

		return slugs;
	};

	// ----------------------------------------------------------------- dom —

	const el = (tag, className, text) => {
		const node = document.createElement(tag);
		if (className) node.className = className;
		if (text != null) node.textContent = text;
		return node;
	};

	const svg = (markup) => {
		const tpl = document.createElement("template");
		tpl.innerHTML = markup.trim(); // static markup only, never user data
		return tpl.content.firstChild;
	};

	const STAR_PATH =
		"M8 1 6.32 6.485H1L5.34 9.66 3.66 15 8 11.68 12.34 15l-1.68-5.34L15 6.485H9.68L8 1Z";

	const starRow = (rating, className) => {
		const wrap = el("span", `pr-stars ${className || ""}`.trim());
		wrap.setAttribute("role", "img");
		wrap.setAttribute("aria-label", `Rated ${rating} out of 5 stars`);
		for (let i = 1; i <= 5; i++) {
			wrap.appendChild(
				svg(
					`<svg class="pr-stars__star${
						i > rating ? " is-empty" : ""
					}" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>`
				)
			);
		}
		return wrap;
	};

	const checkIcon = () =>
		svg(
			'<svg class="pr-check" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M4.5 8.3 7 10.7l4.5-5.2" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
		);

	const thumbIcon = (down) =>
		svg(
			`<svg class="pr-thumb${
				down ? " is-down" : ""
			}" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M1 6.5h2.6V15H1zM4.8 15h6.9c.83 0 1.55-.57 1.75-1.38l1.1-4.5A1.8 1.8 0 0 0 12.8 6.9H9.35l.62-3.05A1.55 1.55 0 0 0 7.2 2.6L4.8 6.7z"/></svg>`
		);

	// same localStorage store the Okendo widget uses, so votes stay in sync
	const VOTES_KEY = "okeReviews-widget-votes";

	const getStoredVotes = () => {
		try {
			return JSON.parse(localStorage.getItem(VOTES_KEY))?.storedVotes || [];
		} catch (e) {
			return [];
		}
	};

	const setStoredVotes = (storedVotes) => {
		try {
			localStorage.setItem(VOTES_KEY, JSON.stringify({ storedVotes }));
		} catch (e) {
			/* private mode — vote still posts, just isn't remembered */
		}
	};

	const googleBadge = () =>
		svg(
			'<svg class="pr-google" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="9" fill="#fff"/><path fill="#4285F4" d="M13.9 9.1c0-.35-.03-.68-.09-1H9v1.9h2.75a2.35 2.35 0 0 1-1.02 1.54v1.28h1.65c.96-.89 1.52-2.2 1.52-3.72z"/><path fill="#34A853" d="M9 14c1.38 0 2.53-.46 3.38-1.24l-1.65-1.28c-.46.3-1.04.49-1.73.49-1.33 0-2.45-.9-2.85-2.1H4.44v1.32A5.1 5.1 0 0 0 9 14z"/><path fill="#FBBC05" d="M6.15 9.87a3.06 3.06 0 0 1 0-1.95V6.6H4.44a5.1 5.1 0 0 0 0 4.6l1.71-1.33z"/><path fill="#EA4335" d="M9 5.82c.75 0 1.42.26 1.95.76l1.46-1.46A5.07 5.07 0 0 0 9 3.8a5.1 5.1 0 0 0-4.56 2.8l1.71 1.32c.4-1.2 1.52-2.1 2.85-2.1z"/></svg>'
		);

	const relativeDate = (iso) => {
		const then = new Date(iso).getTime();
		if (!then) return "";
		const days = Math.floor((Date.now() - then) / 86400000);
		if (days <= 0) return "today";
		if (days === 1) return "1 day ago";
		if (days < 7) return `${days} days ago`;
		const weeks = Math.floor(days / 7);
		if (weeks === 1) return "1 week ago";
		if (weeks < 5) return `${weeks} weeks ago`;
		const months = Math.floor(days / 30.4);
		if (months <= 1) return "1 month ago";
		if (months < 12) return `${months} months ago`;
		const years = Math.floor(days / 365);
		return years <= 1 ? "1 year ago" : `${years} years ago`;
	};

	const paragraphs = (text) =>
		String(text || "")
			.split(/\n+/)
			.map((p) => p.trim())
			.filter(Boolean);

	const readConfig = (host, selector) => {
		const node = host.querySelector(selector);
		if (!node) return null;
		try {
			return JSON.parse(node.textContent);
		} catch (e) {
			return null;
		}
	};

	// After insertion: clamp the body and add a Read More toggle if it overflows.
	// Keeps re-measuring on size changes (viewport/device switches), so the
	// button appears/disappears as the clamp threshold moves.
	// setTimeout (not rAF) so it measures after append and works in background tabs.
	const initReadMore = (body, button) => {
		body.classList.add("is-clamped");
		const measure = () => {
			if (body.classList.contains("is-expanded")) return;
			button.hidden = body.scrollHeight <= body.clientHeight + 2;
		};
		button.addEventListener("click", () => {
			const expanded = body.classList.toggle("is-expanded");
			body.classList.toggle("is-clamped", !expanded);
			button.textContent = expanded ? "Read Less" : "Read More";
			button.setAttribute("aria-expanded", expanded ? "true" : "false");
		});
		setTimeout(measure, 0);
		if (typeof ResizeObserver !== "undefined") {
			new ResizeObserver(measure).observe(body);
		}
	};

	// ------------------------------------------------- <claims-reviews> ----

	class ClaimsReviews extends HTMLElement {
		connectedCallback() {
			// defer a tick so children are parsed when the element upgrades
			// mid-parse (setTimeout, not rAF — rAF stalls in background tabs)
			if (this._initialized) return;
			this._initialized = true;
			setTimeout(() => this.init(), 0);
		}

		init() {
			const config = readConfig(this, "[data-cr-config]");
			if (!config || !config.autofill || !config.subscriberId || !config.productId) {
				return;
			}
			this.config = config;
			this.categories = normalizeCategories(config.categories);

			this.dialog = this.querySelector("[data-lightbox]");
			this.dialogBody = this.querySelector("[data-lightbox-body]");
			if (this.dialog) {
				this.dialog.addEventListener("click", (e) => {
					if (e.target === this.dialog) this.dialog.close();
				});
				const closeBtn = this.dialog.querySelector("[data-lightbox-close]");
				if (closeBtn) {
					closeBtn.addEventListener("click", () => this.dialog.close());
				}
			}

			// server-rendered card cart buttons
			this.querySelectorAll(".js-claims-add-to-cart").forEach((btn) => {
				btn.addEventListener("click", () => this.addToCart(btn));
			});

			loadOkendoData(config.subscriberId, config.productId)
				.then((data) => this.render(data.reviews || [], data.media || []))
				.catch(() => this.removeSlots()); // rows keep curated cards

		}

		// full review in the shared lightbox — cards stay clamped so the row
		// layout never shifts (consistent on desktop and mobile)
		openReviewLightbox(review, mediaItem) {
			if (!this.dialog || !this.dialogBody) return;
			this.dialogBody.textContent = "";

			if (mediaItem?.imageUrls) {
				const img = document.createElement("img");
				img.src =
					mediaItem.imageUrls.thumbnailUrl?.replace("120x120", "800x800") ||
					mediaItem.imageUrls.largeUrl;
				img.alt = mediaItem.imageAlt || "Customer review photo";
				const large = mediaItem.imageUrls.largeUrl;
				if (large && large !== img.src) {
					const hi = new Image();
					hi.onload = () => {
						img.src = large;
					};
					hi.src = large;
				}
				this.dialogBody.appendChild(img);
			}

			const wrap = el("div", "s-product-reviews__lightbox__review");
			wrap.appendChild(starRow(review.rating || 5, "s-claims-reviews__stars"));
			if (review.title) {
				wrap.appendChild(
					el("h3", "s-product-reviews__lightbox__review__title", review.title)
				);
			}
			const body = el("div", "s-product-reviews__lightbox__review__body");
			paragraphs(review.body).forEach((p) => body.appendChild(el("p", "", p)));
			wrap.appendChild(body);

			// same footer as the cards: reviewer name + add-to-cart button
			const footer = el("div", "s-claims-reviews__card__footer");
			const name = review.reviewer?.displayName;
			if (name) {
				footer.appendChild(el("p", "s-claims-reviews__card__name", `- ${name}`));
			}
			if (this.config.cartVariantId) {
				footer.appendChild(this.buildCartButton());
			}
			wrap.appendChild(footer);

			this.dialogBody.appendChild(wrap);
			this.dialog.showModal();
		}

		removeSlots() {
			this.querySelectorAll("[data-review-slot]").forEach((slot) =>
				slot.remove()
			);
		}

		render(reviews, media) {
			const reviewById = {};
			reviews.forEach((review) => (reviewById[review.reviewId] = review));

			const rows = this.querySelectorAll("[data-claims-row]");
			rows.forEach((row) => {
				const slug = row.dataset.category;

				// signatures of curated cards, to skip duplicate reviews
				const sigs = new Set(
					Array.from(row.querySelectorAll("[data-review-sig]")).map((card) =>
						card.dataset.reviewSig.toLowerCase()
					)
				);

				// classified pool for this category, minus curated duplicates
				const pool = reviews.filter((review) => {
					if (!review._cats) {
						review._cats = classifyReview(review, this.categories);
					}
					if (!review._cats.has(slug)) return false;
					const sig = `${review.reviewer?.displayName || ""}|${
						review.title || ""
					}`
						.trim()
						.toLowerCase();
					return !sigs.has(sig);
				});

				// dynamic photo reviews: swap curated Photo + Quote cards for
				// customer photos whose review matches this category; curated
				// content stays as the fallback when no photo is available
				if (this.config.dynamicPhotos) {
					const photoCards = Array.from(
						row.querySelectorAll("[data-photo-card]")
					);
					const photos = (media || []).filter((item) => {
						if (item.type !== "image" || !item.imageUrls) return false;
						const owner = reviewById[item.reviewId];
						return owner && owner._cats.has(slug);
					});
					let photoIndex = 0;
					photoCards.forEach((card) => {
						const item = photos[photoIndex];
						if (!item) return;
						photoIndex++;
						card.replaceWith(
							this.buildPhotoCard(item, reviewById[item.reviewId])
						);
					});
				}

				const slots = Array.from(row.querySelectorAll("[data-review-slot]"));
				if (slots.length) {
					// slots define WHERE dynamic reviews appear — each fills with
					// the next unused review from the category pool (newest first)
					const used = new Set();
					slots.forEach((slot) => {
						const review = pool.find((r) => !used.has(r.reviewId)) || null;
						if (review) {
							used.add(review.reviewId);
							slot.replaceWith(this.buildCard(review));
						} else {
							slot.remove();
						}
					});
					return; // no tail-append when slots are present
				}

				// no slots in this row: legacy behavior, append up to maxAuto
				const max = this.config.maxAuto || 0;
				pool.slice(0, max).forEach((review) => {
					row.appendChild(this.buildCard(review));
				});
			});
		}

		buildCard(review) {
			const card = el("div", "s-claims-reviews__card is-text is-auto");
			card.appendChild(starRow(review.rating || 5, "s-claims-reviews__stars"));

			const content = el("div", "s-claims-reviews__card__content");
			if (review.title) {
				content.appendChild(
					el("h3", "s-claims-reviews__card__title", review.title)
				);
			}
			const body = el("div", "s-claims-reviews__card__body");
			paragraphs(review.body).forEach((p) => body.appendChild(el("p", "", p)));
			content.appendChild(body);

			const readMore = el("button", "s-claims-reviews__card__more", "Read More");
			readMore.type = "button";
			readMore.hidden = true;
			readMore.setAttribute("aria-haspopup", "dialog");
			readMore.addEventListener("click", () => this.openReviewLightbox(review));
			content.appendChild(readMore);
			card.appendChild(content);

			const footer = el("div", "s-claims-reviews__card__footer");
			const name = review.reviewer?.displayName || "";
			if (name) {
				footer.appendChild(
					el("p", "s-claims-reviews__card__name", `- ${name}`)
				);
			}
			if (this.config.cartVariantId) {
				footer.appendChild(this.buildCartButton());
			}
			card.appendChild(footer);

			// card stays clamped — Read More opens the lightbox instead of
			// expanding in place, so the row layout never shifts
			body.classList.add("is-clamped");
			const measure = () => {
				readMore.hidden = body.scrollHeight <= body.clientHeight + 2;
			};
			setTimeout(measure, 0);
			if (typeof ResizeObserver !== "undefined") {
				new ResizeObserver(measure).observe(body);
			}

			return card;
		}

		// adds the product exactly like the buy box: submits the page's main
		// product form (keeps selected variant/plan, preorder handling, and
		// opens the minicart via the theme's own handler)
		addToCart(button) {
			this.setCartLoading(button, true);

			const forms = Array.from(document.querySelectorAll("form.c-product-form"));
			const mainForm = forms.find((f) => f.offsetParent !== null) || forms[0];
			if (mainForm) {
				mainForm.requestSubmit();
			} else if (this.config.cartVariantId) {
				// fallback: minimal add-to-cart form for the default variant,
				// submitted through the theme's delegated handler
				const form = document.createElement("form");
				form.action = "/cart/add";
				form.method = "post";
				form.hidden = true;
				const input = document.createElement("input");
				input.type = "hidden";
				input.name = "id";
				input.value = this.config.cartVariantId;
				form.appendChild(input);
				document.body.appendChild(form);
				form.requestSubmit();
				setTimeout(() => form.remove(), 1000);
			} else {
				this.setCartLoading(button, false);
				return;
			}

			this.watchCartAdded(button);
		}

		setCartLoading(button, on) {
			if (button) {
				button.classList.toggle("is-loading", on);
				button.disabled = on;
			}
		}

		// the theme opens the minicart (root.is-minicart-active) once the add
		// succeeds — use that as the completion signal; time out as a fallback
		watchCartAdded(button) {
			const root = document.documentElement;
			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				observer.disconnect();
				clearTimeout(timer);
				this.setCartLoading(button, false);
				if (this.dialog?.open) this.dialog.close();
			};
			const observer = new MutationObserver(() => {
				if (root.classList.contains("is-minicart-active")) finish();
			});
			observer.observe(root, {
				attributes: true,
				attributeFilter: ["class"],
			});
			const timer = setTimeout(finish, 5000);
		}

		buildPhotoCard(item, review) {
			const card = el("div", "s-claims-reviews__card is-media is-photo");
			card.setAttribute("role", "button");
			card.tabIndex = 0;
			card.setAttribute("aria-label", "Read this photo review");

			const img = document.createElement("img");
			img.src =
				item.imageUrls.thumbnailUrl?.replace("120x120", "800x800") ||
				item.imageUrls.largeUrl;
			img.alt = item.imageAlt || "Customer review photo";
			img.decoding = "async";
			card.appendChild(img);

			const overlay = el("div", "s-claims-reviews__card__overlay");
			if (review.body) {
				overlay.appendChild(
					el("p", "s-claims-reviews__card__quote", review.body)
				);
			}
			const more = el("button", "s-claims-reviews__card__more", "Read More");
			more.type = "button";
			more.setAttribute("aria-haspopup", "dialog");
			overlay.appendChild(more);
			if (review.reviewer?.displayName) {
				overlay.appendChild(
					el("p", "s-claims-reviews__card__handle", review.reviewer.displayName)
				);
			}
			card.appendChild(overlay);

			const open = () => this.openReviewLightbox(review, item);
			card.addEventListener("click", open);
			card.addEventListener("keydown", (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					open();
				}
			});

			return card;
		}

		buildCartButton() {
			const btn = el("button", "s-claims-reviews__cart__btn");
			btn.type = "button";
			btn.setAttribute("aria-label", "Add to cart");
			btn.addEventListener("click", () => this.addToCart(btn));
			btn.appendChild(
				svg(
					'<svg viewBox="0 0 20 17" fill="none" aria-hidden="true"><path fill="currentColor" d="M19.9749 4.09798L17.7645 11.6401C17.643 12.0521 17.3831 12.4153 17.0242 12.6746C16.6653 12.9339 16.227 13.0751 15.7758 13.0769H7.25512C6.80257 13.0767 6.3625 12.9362 6.00203 12.6768C5.64156 12.4174 5.38047 12.0533 5.25859 11.6401L2.23447 1.30769H0.689651C0.506744 1.30769 0.331329 1.2388 0.201994 1.11618C0.0726594 0.993565 0 0.827257 0 0.653846C0 0.480435 0.0726594 0.314127 0.201994 0.191507C0.331329 0.0688872 0.506744 0 0.689651 0H2.7586C2.90938 0 3.05601 0.0467922 3.17605 0.133292C3.29609 0.219791 3.38292 0.341203 3.42325 0.478942L4.24049 3.26923H19.3102C19.4165 3.26921 19.5214 3.2925 19.6167 3.33727C19.7119 3.38204 19.795 3.44709 19.8593 3.52733C19.9237 3.60757 19.9676 3.70083 19.9876 3.79982C20.0077 3.89881 20.0033 4.00085 19.9749 4.09798ZM6.89651 14.3846C6.62371 14.3846 6.35703 14.4613 6.13021 14.605C5.90338 14.7487 5.72659 14.9529 5.6222 15.1919C5.5178 15.4308 5.49049 15.6938 5.54371 15.9474C5.59693 16.2011 5.72829 16.4341 5.92119 16.617C6.11409 16.7999 6.35986 16.9244 6.62742 16.9749C6.89498 17.0253 7.17231 16.9994 7.42434 16.9005C7.67638 16.8015 7.89179 16.6339 8.04335 16.4188C8.19491 16.2038 8.27581 15.9509 8.27581 15.6923C8.27581 15.3455 8.13049 15.0129 7.87182 14.7676C7.61315 14.5224 7.26232 14.3846 6.89651 14.3846ZM15.862 14.3846C15.5892 14.3846 15.3225 14.4613 15.0957 14.605C14.8688 14.7487 14.6921 14.9529 14.5877 15.1919C14.4833 15.4308 14.4559 15.6938 14.5092 15.9474C14.5624 16.2011 14.6938 16.4341 14.8867 16.617C15.0795 16.7999 15.3253 16.9244 15.5929 16.9749C15.8604 17.0253 16.1378 16.9994 16.3898 16.9005C16.6418 16.8015 16.8573 16.6339 17.0088 16.4188C17.1604 16.2038 17.2413 15.9509 17.2413 15.6923C17.2413 15.3455 17.0959 15.0129 16.8373 14.7676C16.5786 14.5224 16.2278 14.3846 15.862 14.3846Z"/></svg>'
				)
			);
			return btn;
		}
	}

	// ------------------------------------------------ <product-reviews> ----

	class ProductReviews extends HTMLElement {
		connectedCallback() {
			if (this._initialized) return;
			this._initialized = true;
			setTimeout(() => this.init(), 0);
		}

		init() {
			const config = readConfig(this, "[data-pr-config]");
			if (!config || !config.subscriberId || !config.productId) return;
			this.config = config;
			this.categories = normalizeCategories(config.categories);
			this.active = "all";
			this.visible = config.perPage || 5;
			this.reviews = [];
			this.media = [];

			this.listEl = this.querySelector("[data-review-list]");
			this.mediaEl = this.querySelector("[data-review-media]");
			this.stripEl = this.querySelector("[data-media-strip]");
			this.errorEl = this.querySelector("[data-error]");
			this.moreBtn = this.querySelector("[data-load-more]");
			this.dialog = this.querySelector("[data-lightbox]");
			this.dialogBody = this.querySelector("[data-lightbox-body]");

			this.bindEvents();

			loadOkendoData(config.subscriberId, config.productId)
				.then((data) => {
					this.reviews = data.reviews || [];
					this.media = data.media || [];
					this.reviews.forEach((review) => {
						review._cats = classifyReview(review, this.categories);
					});
					this.reviewById = {};
					this.reviews.forEach((r) => (this.reviewById[r.reviewId] = r));
					this.allOrder = this.buildAllOrder();
					this.renderMedia();
					this.renderList();
					this.warmLightboxImages();
				})
				.catch(() => this.showError());
		}

		bindEvents() {
			this.querySelectorAll("[data-category]").forEach((chip) => {
				chip.addEventListener("click", () => this.setCategory(chip));
			});

			if (this.moreBtn) {
				this.moreBtn.addEventListener("click", () => {
					this.visible += this.config.perPage || 5;
					this.renderList();
				});
			}

			if (this.dialog) {
				this.dialog.addEventListener("click", (e) => {
					if (e.target === this.dialog) this.closeLightbox();
				});
				// the 'close' event is unreliable on this page (suppressed by a
				// third-party script), so watch the open attribute instead —
				// clearing on close is what stops lightbox video playback
				this.dialog.addEventListener("close", () => this.clearLightbox());
				new MutationObserver(() => {
					if (!this.dialog.open) this.clearLightbox();
				}).observe(this.dialog, {
					attributes: true,
					attributeFilter: ["open"],
				});
				const closeBtn = this.dialog.querySelector("[data-lightbox-close]");
				if (closeBtn) {
					closeBtn.addEventListener("click", () => this.closeLightbox());
				}
			}

			// server-rendered UGC video thumbs — open in the media lightbox,
			// same as review photos
			this.querySelectorAll("[data-ugc-thumb]").forEach((thumb) => {
				thumb.addEventListener("click", () => this.openVideoLightbox(thumb));
			});
		}

		setCategory(chip) {
			this.active = chip.dataset.category;
			this.visible = this.config.perPage || 5;
			this.querySelectorAll("[data-category]").forEach((btn) => {
				const isActive = btn === chip;
				btn.classList.toggle("is-active", isActive);
				btn.setAttribute("aria-pressed", isActive ? "true" : "false");
			});
			this.renderMedia();
			this.renderList();
		}

		filtered() {
			if (this.active === "all") return this.allOrder || this.reviews;
			return this.reviews.filter((review) => review._cats.has(this.active));
		}

		// ALL feed: round-robin across the category buckets (one Breathing,
		// one Performance, one Environment, repeat — plus uncategorized), so
		// ALL reads as a mix instead of mirroring the biggest category
		buildAllOrder() {
			const buckets = this.categories.map((cat) =>
				this.reviews.filter((review) => review._cats.has(cat.slug))
			);
			buckets.push(this.reviews.filter((review) => review._cats.size === 0));

			const pointers = buckets.map(() => 0);
			const seen = new Set();
			const out = [];
			let added = true;
			while (added) {
				added = false;
				buckets.forEach((bucket, i) => {
					while (
						pointers[i] < bucket.length &&
						seen.has(bucket[pointers[i]].reviewId)
					) {
						pointers[i]++;
					}
					if (pointers[i] < bucket.length) {
						const review = bucket[pointers[i]++];
						seen.add(review.reviewId);
						out.push(review);
						added = true;
					}
				});
			}
			return out;
		}

		thumbUrl(item) {
			return (
				item.imageUrls.thumbnailUrl?.replace("120x120", "400x400") ||
				item.imageUrls.largeUrl
			);
		}

		// Okendo image URLs carry a 1-year cache header, so fetching the large
		// versions in the background makes the lightbox instant on click
		warmLightboxImages() {
			setTimeout(() => {
				this.media.forEach((item) => {
					const url = item.imageUrls?.largeUrl;
					if (url) new Image().src = url;
				});
			}, 2500);
		}

		renderMedia() {
			if (!this.stripEl) return;

			// UGC blocks (server-rendered): toggle by category
			this.querySelectorAll("[data-ugc-thumb]").forEach((thumb) => {
				const cats = (thumb.dataset.categories || "").split(",");
				thumb.hidden = this.active !== "all" && !cats.includes(this.active);
			});

			// customer photos from the API
			if (!this.mediaEl) return;
			this.mediaEl.textContent = "";
			const limit = this.config.mediaLimit || 12;
			const items = this.media
				.filter((item) => {
					if (item.type !== "image" || !item.imageUrls) return false;
					if (this.active === "all") return true;
					const review = this.reviewById[item.reviewId];
					return review && review._cats.has(this.active);
				})
				.slice(0, limit);

			items.forEach((item) => {
				const btn = el("button", "s-product-reviews__thumb");
				btn.type = "button";
				btn.setAttribute("aria-label", "View review photo");
				const img = document.createElement("img");
				// Okendo's image CDN takes ~5s per image on the FIRST hit but
				// responds with a 1-year cache header — load eagerly (never
				// lazy) so downloads start at page load, not on scroll
				img.decoding = "async";
				img.className = "is-loading";
				img.addEventListener(
					"load",
					() => img.classList.remove("is-loading"),
					{ once: true }
				);
				img.src = this.thumbUrl(item);
				img.alt = item.imageAlt || "Customer review photo";
				btn.appendChild(img);
				btn.addEventListener("click", () => this.openImageLightbox(item, btn));
				this.mediaEl.appendChild(btn);
			});

			const hasContent =
				items.length ||
				Array.from(this.querySelectorAll("[data-ugc-thumb]")).some(
					(thumb) => !thumb.hidden
				);
			this.stripEl.classList.toggle("is-empty", !hasContent);
		}

		renderList() {
			if (!this.listEl) return;
			this.listEl.textContent = "";

			const items = this.filtered();
			if (!items.length) {
				this.listEl.appendChild(
					el(
						"p",
						"s-product-reviews__empty",
						this.reviews.length
							? "No reviews in this category yet."
							: "No reviews yet."
					)
				);
				if (this.moreBtn) this.moreBtn.hidden = true;
				return;
			}

			items.slice(0, this.visible).forEach((review) => {
				this.listEl.appendChild(this.renderItem(review));
			});

			if (this.moreBtn) this.moreBtn.hidden = this.visible >= items.length;
		}

		renderItem(review) {
			const item = el("article", "s-product-reviews__item");

			// header: avatar + name/verified + date
			const head = el("div", "s-product-reviews__item__head");
			const avatar = el("div", "s-product-reviews__avatar");
			const reviewer = review.reviewer || {};
			// initials always render as the base layer; the profile photo (when
			// present) covers them once loaded and is dropped if it fails
			const initials = (reviewer.displayName || "?")
				.split(/\s+/)
				.map((part) => part.charAt(0))
				.join("")
				.slice(0, 2)
				.toUpperCase();
			avatar.appendChild(el("span", "", initials));
			if (reviewer.avatarUrl) {
				const img = document.createElement("img");
				img.src = reviewer.avatarUrl;
				img.alt = "";
				img.addEventListener("error", () => img.remove());
				avatar.appendChild(img);
			}
			if (reviewer.socialConnection === "google") {
				avatar.appendChild(googleBadge());
			}
			head.appendChild(avatar);

			const meta = el("div", "s-product-reviews__item__meta");
			meta.appendChild(
				el("p", "s-product-reviews__item__name", reviewer.displayName || "")
			);
			const verifiedLabel =
				reviewer.verifiedStatus ||
				(reviewer.isVerified ? "Verified Reviewer" : "");
			if (verifiedLabel) {
				const verified = el("p", "s-product-reviews__item__verified");
				verified.appendChild(el("span", "", verifiedLabel));
				verified.appendChild(checkIcon());
				meta.appendChild(verified);
			}
			head.appendChild(meta);

			head.appendChild(
				el(
					"p",
					"s-product-reviews__item__date",
					relativeDate(review.dateCreated)
				)
			);
			item.appendChild(head);

			item.appendChild(starRow(review.rating || 5, "s-product-reviews__stars"));

			if (review.title) {
				item.appendChild(
					el("h3", "s-product-reviews__item__title", review.title)
				);
			}

			const body = el("div", "s-product-reviews__item__body");
			paragraphs(review.body).forEach((p) => body.appendChild(el("p", "", p)));
			item.appendChild(body);

			const readMore = el(
				"button",
				"s-product-reviews__item__more",
				"Read More"
			);
			readMore.type = "button";
			readMore.hidden = true;
			item.appendChild(readMore);
			initReadMore(body, readMore);

			item.appendChild(this.buildHelpfulRow(review));

			return item;
		}

		// functional helpful voting against the same endpoint and vote-type
		// semantics the Okendo widget uses (upvote/downvote, cancel, swap)
		buildHelpfulRow(review) {
			const row = el("div", "s-product-reviews__item__helpful");
			row.appendChild(el("span", "", "Was this helpful?"));

			let current =
				getStoredVotes().find(
					(v) => v.itemId === review.reviewId && v.contentType === "review"
				)?.voteType || "";

			const makeBtn = (dir, label) => {
				const btn = el("button", "s-product-reviews__vote");
				btn.type = "button";
				btn.setAttribute("aria-label", label);
				btn.appendChild(thumbIcon(dir === "downvote"));
				btn.appendChild(
					el(
						"span",
						"",
						String(
							(dir === "upvote" ? review.helpfulCount : review.unhelpfulCount) ||
								0
						)
					)
				);
				return btn;
			};
			const upBtn = makeBtn("upvote", "Yes, this review was helpful");
			const downBtn = makeBtn("downvote", "No, this review was not helpful");

			const sync = () => {
				upBtn.classList.toggle("is-voted", current === "upvote");
				downBtn.classList.toggle("is-voted", current === "downvote");
				upBtn.setAttribute("aria-pressed", current === "upvote" ? "true" : "false");
				downBtn.setAttribute(
					"aria-pressed",
					current === "downvote" ? "true" : "false"
				);
			};

			const vote = async (dir) => {
				const type = current
					? current === dir
						? `${dir}-cancel`
						: `swap-to-${dir}`
					: dir;
				upBtn.disabled = downBtn.disabled = true;
				try {
					const res = await fetch(
						`${API_BASE}/stores/${this.config.subscriberId}/reviews/${review.reviewId}/vote`,
						{
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({ voteType: type }),
						}
					);
					if (!res.ok) throw new Error(`Okendo vote ${res.status}`);
					const json = await res.json();
					review.helpfulCount = json.review?.helpfulCount ?? review.helpfulCount;
					review.unhelpfulCount =
						json.review?.unhelpfulCount ?? review.unhelpfulCount;
					upBtn.lastChild.textContent = String(review.helpfulCount || 0);
					downBtn.lastChild.textContent = String(review.unhelpfulCount || 0);

					current = type.endsWith("-cancel") ? "" : dir;
					const votes = getStoredVotes().filter(
						(v) => !(v.itemId === review.reviewId && v.contentType === "review")
					);
					if (current) {
						votes.push({
							itemId: review.reviewId,
							contentType: "review",
							voteType: current,
						});
					}
					setStoredVotes(votes);
					cacheSet(`okReviews:${this.config.productId}`, {
						reviews: this.reviews,
						media: this.media,
					});
					sync();
				} catch (e) {
					/* keep previous state on failure */
				} finally {
					upBtn.disabled = downBtn.disabled = false;
				}
			};

			upBtn.addEventListener("click", () => vote("upvote"));
			downBtn.addEventListener("click", () => vote("downvote"));
			sync();

			row.appendChild(upBtn);
			row.appendChild(downBtn);
			return row;
		}

		// ------------------------------------------------------- lightbox —

		openImageLightbox(item, opener) {
			if (!this.dialog) return;
			this.opener = opener;
			this.clearLightbox();

			// paint the strip's already-cached crop instantly, then swap in the
			// large version (pre-warmed in the background after load)
			const img = document.createElement("img");
			img.src = this.thumbUrl(item);
			img.alt = item.imageAlt || "Customer review photo";
			const largeUrl = item.imageUrls.largeUrl || item.imageUrls.fullSizeUrl;
			if (largeUrl && largeUrl !== img.src) {
				const hi = new Image();
				hi.onload = () => {
					img.src = largeUrl;
				};
				hi.src = largeUrl;
			}
			this.dialogBody.appendChild(img);

			const review = this.reviewById[item.reviewId];
			if (review) {
				const caption = el("div", "s-product-reviews__lightbox__caption");
				caption.appendChild(
					starRow(review.rating || 5, "s-product-reviews__stars")
				);
				if (review.title) caption.appendChild(el("p", "", review.title));
				if (review.reviewer?.displayName) {
					caption.appendChild(
						el(
							"p",
							"s-product-reviews__lightbox__name",
							`- ${review.reviewer.displayName}`
						)
					);
				}
				this.dialogBody.appendChild(caption);
			}

			this.dialog.showModal();
		}

		openVideoLightbox(thumb) {
			if (!this.dialog) return;
			this.opener = thumb;
			this.clearLightbox();

			const { videoSrc, videoHost, videoId } = thumb.dataset;
			if (videoSrc) {
				const video = document.createElement("video");
				video.src = videoSrc;
				video.controls = true;
				video.autoplay = true;
				video.playsInline = true;
				video.setAttribute("playsinline", "");
				if (thumb.dataset.poster) video.poster = thumb.dataset.poster;
				this.dialogBody.appendChild(video);
			} else if (videoHost && videoId) {
				const iframe = document.createElement("iframe");
				iframe.src =
					videoHost === "youtube"
						? `https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`
						: `https://player.vimeo.com/video/${videoId}?autoplay=1&playsinline=1`;
				iframe.allow = "autoplay; fullscreen; picture-in-picture";
				iframe.allowFullscreen = true;
				iframe.title = thumb.getAttribute("aria-label") || "Review video";
				iframe.className = "is-embed";
				this.dialogBody.appendChild(iframe);
			} else {
				return;
			}

			// same caption treatment as review photos (admin-entered on the block)
			const { caption, reviewer, rating } = thumb.dataset;
			if (caption || reviewer) {
				const cap = el("div", "s-product-reviews__lightbox__caption");
				cap.appendChild(
					starRow(parseInt(rating, 10) || 5, "s-product-reviews__stars")
				);
				if (caption) cap.appendChild(el("p", "", caption));
				if (reviewer) {
					cap.appendChild(
						el("p", "s-product-reviews__lightbox__name", `- ${reviewer}`)
					);
				}
				this.dialogBody.appendChild(cap);
			}

			this.dialog.showModal();
		}

		closeLightbox() {
			if (this.dialog?.open) this.dialog.close();
			this.clearLightbox();
		}

		clearLightbox() {
			if (this.dialogBody) this.dialogBody.textContent = ""; // stops playback
		}

		showError() {
			if (this.listEl) this.listEl.textContent = "";
			if (this.errorEl) this.errorEl.hidden = false;
			if (this.moreBtn) this.moreBtn.hidden = true;
			if (this.stripEl) this.stripEl.classList.add("is-empty");
		}
	}

	if (!customElements.get("claims-reviews")) {
		customElements.define("claims-reviews", ClaimsReviews);
	}
	if (!customElements.get("product-reviews")) {
		customElements.define("product-reviews", ProductReviews);
	}
})();
